import { useState, useMemo, useEffect, useCallback } from 'react';
import DashboardLayout from '@/components/feature/DashboardLayout';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/lib/supabase';
import {
  ESFERAS,
  periodicidadeDaVigencia,
  type AlvaraTemplate,
  type EsferaAlvara,
} from '@/lib/alvaras';
import {
  useCatalogo,
  type CategoriaAlvara,
  type Estado,
  type ItemDeInfraestrutura,
} from '@/lib/catalogo';
import CampoPeriodo from '@/components/base/CampoPeriodo';
import CampoAvisoVencimento from '@/components/base/CampoAvisoVencimento';
import { useJanelaDeAlerta } from '@/lib/notificacoes';
import { periodoDeColunas, type PeriodoParcial } from '@/lib/periodo';
import { salvarTemplateDeAlvara } from '@/lib/infraestruturaCatalogoRepo';

const periodicidades = [
  'mensal',
  'bimestral',
  'trimestral',
  'semestral',
  'anual',
  'bienal',
  'quinquenal',
  'unico',
];

const fmtData = (iso: string) => {
  const [y, m, d] = iso.split('-');
  return `${d}/${m}/${y}`;
};

function mapRowToTemplate(row: any): AlvaraTemplate {
  return {
    id: row.id,
    nome: row.nome,
    descricao: row.descricao || '',
    categoria: row.categoria,
    abrangencia: row.abrangencia,
    // `esfera` e `abrangencia` sao eixos diferentes (migration 0048): quem
    // emite, e onde e exigido. O fallback nao adivinha — usa o padrao da
    // coluna, que a migration ja preencheu para todo registro existente.
    esfera: (row.esfera || 'municipal') as EsferaAlvara,
    estados: row.estados || [],
    todasCidades: row.todas_cidades ?? true,
    cidadesEspecificas: row.cidades_especificas || [],
    periodicidade: row.periodicidade,
    obrigatorio: row.obrigatorio ?? true,
    orgaoEmissor: row.orgao_emissor || '',
    vigencia: periodoDeColunas(row, 'vigencia'),
    prazo: periodoDeColunas(row, 'prazo'),
    requerDocumentoAnexo: row.requer_documento_anexo ?? true,
    diasAntecedenciaAviso: row.dias_antecedencia_aviso ?? null,
    tempoMedioGeracao: periodoDeColunas(row, 'tempo_medio_geracao'),
    aplicaAoCondominioGeral: row.aplica_ao_condominio_geral ?? true,
    itens: Array.isArray(row.itens) ? row.itens : [],
    dataCriacao: row.data_criacao || '',
    ativo: row.ativo ?? true,
  };
}

function AlvarasConfigTab() {
  // Estados e categorias vêm do catálogo no banco (ADR-0011), não de um array
  // no código: acrescentar uma categoria de alvará é dado, não deploy.
  const { itens: estados } = useCatalogo<Estado>('estados');
  const { itens: categorias } = useCatalogo<CategoriaAlvara>('categorias_alvara');
  const [templates, setTemplates] = useState<AlvaraTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [busca, setBusca] = useState('');
  const [filtroCategoria, setFiltroCategoria] = useState('');
  const [filtroEstado, setFiltroEstado] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editTarget, setEditTarget] = useState<AlvaraTemplate | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<AlvaraTemplate | null>(null);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<{ msg: string; type: 'success' | 'info' } | null>(null);

  const showToast = (msg: string, type: 'success' | 'info' = 'info') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  const fetchTemplates = useCallback(async () => {
    setLoading(true);
    setFetchError(null);
    try {
      // Os itens vinculados vem numa segunda consulta, e nao num embed: o
      // PostgREST nomeia o embed pela constraint, que muda de nome quando
      // alguem a recria — e quebra em silencio, devolvendo o template sem
      // vinculo nenhum.
      const [{ data, error }, { data: vinculos }] = await Promise.all([
        supabase.from('alvaras_templates').select('*').order('data_criacao', { ascending: false }),
        supabase.from('alvara_template_itens').select('template_id, item_id'),
      ]);

      if (error) {
        setFetchError(error.message);
      } else if (data) {
        const porTemplate = new Map<string, string[]>();
        for (const v of vinculos ?? []) {
          const atuais = porTemplate.get(v.template_id as string) ?? [];
          atuais.push(v.item_id as string);
          porTemplate.set(v.template_id as string, atuais);
        }
        setTemplates(
          data.map((row) => mapRowToTemplate({ ...row, itens: porTemplate.get(row.id) ?? [] })),
        );
      }
    } catch (e: any) {
      setFetchError(e.message || 'Erro ao carregar templates');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  const filtrados = useMemo(() => {
    return templates.filter((t) => {
      const matchBusca =
        t.nome.toLowerCase().includes(busca.toLowerCase()) ||
        t.descricao.toLowerCase().includes(busca.toLowerCase()) ||
        t.orgaoEmissor.toLowerCase().includes(busca.toLowerCase());
      const matchCat = filtroCategoria ? t.categoria === filtroCategoria : true;
      const matchEst = filtroEstado ? t.estados.includes(filtroEstado) : true;
      return matchBusca && matchCat && matchEst;
    });
  }, [templates, busca, filtroCategoria, filtroEstado]);

  const handleSave = async (data: AlvaraTemplate) => {
    setSaving(true);
    try {
      // Uma chamada so, e nao insert/update separados: `salvar_alvara_template`
      // grava o template e os itens vinculados na mesma transacao, que e o que
      // a constraint trigger da migration 0048 exige. Duas requisicoes PostgREST
      // seriam duas transacoes, e a primeira seria recusada no commit.
      await salvarTemplateDeAlvara({
        id: editTarget?.id,
        nome: data.nome,
        descricao: data.descricao,
        categoria: data.categoria,
        esfera: data.esfera,
        abrangencia: data.abrangencia,
        estados: data.estados,
        todasCidades: data.todasCidades,
        cidadesEspecificas: data.todasCidades ? [] : data.cidadesEspecificas,
        // Derivada da vigencia: sem isso a pessoa escolhe "anual" no rotulo e
        // "2 anos" no Periodo, e o registro afirma as duas coisas.
        periodicidade: periodicidadeDaVigencia(data.vigencia),
        obrigatorio: data.obrigatorio,
        orgaoEmissor: data.orgaoEmissor,
        vigencia: data.vigencia,
        prazo: data.prazo,
        requerDocumentoAnexo: data.requerDocumentoAnexo,
        diasAntecedenciaAviso: data.diasAntecedenciaAviso,
        tempoMedioGeracao: data.tempoMedioGeracao,
        aplicaAoCondominioGeral: data.aplicaAoCondominioGeral,
        itens: data.itens,
        ativo: data.ativo,
      });

      showToast(
        editTarget
          ? `"${data.nome}" atualizado com sucesso.`
          : `"${data.nome}" cadastrado com sucesso.`,
        'success',
      );

      await fetchTemplates();
      setShowForm(false);
      setEditTarget(null);
    } catch (e: any) {
      showToast(e.message || 'Erro ao salvar', 'info');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setSaving(true);
    try {
      const { error } = await supabase.from('alvaras_templates').delete().eq('id', deleteTarget.id);

      if (error) throw error;
      showToast(`"${deleteTarget.nome}" excluído com sucesso.`, 'success');
      await fetchTemplates();
      setDeleteTarget(null);
    } catch (e: any) {
      showToast(e.message || 'Erro ao excluir', 'info');
    } finally {
      setSaving(false);
    }
  };

  const estadoNome = (uf: string) => estados.find((e) => e.uf === uf)?.nome || uf;

  const categoriaBadge = (cat: string) => {
    const map: Record<string, string> = {
      Bombeiros: 'bg-red-50 text-red-700 border-red-200',
      Prefeitura: 'bg-accent-100 text-accent-900 border-accent-300',
      'Vigilância Sanitária': 'bg-green-50 text-green-700 border-green-200',
      Ambiental: 'bg-secondary-100 text-secondary-900 border-secondary-200',
    };
    return map[cat] || 'bg-background-100 text-foreground-700 border-background-300';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="flex flex-col items-center gap-4">
          <div className="w-10 h-10 border-2 border-primary-500 border-t-transparent rounded-full animate-spin"></div>
          <p className="text-sm text-foreground-500">Carregando templates...</p>
        </div>
      </div>
    );
  }

  if (fetchError) {
    return (
      <div className="flex flex-col items-center gap-4 py-20 text-center">
        <div className="w-14 h-14 rounded-full bg-red-50 flex items-center justify-center">
          <i className="ri-error-warning-line text-red-500 text-2xl"></i>
        </div>
        <h3 className="text-lg font-semibold text-foreground-900">Erro ao carregar templates</h3>
        <p className="text-sm text-foreground-600 max-w-md">{fetchError}</p>
        <button
          onClick={fetchTemplates}
          className="px-4 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap"
        >
          Tentar novamente
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      {/* cabeçalho */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-base font-semibold text-foreground-900">Configuração de Alvarás</h2>
          <p className="text-sm text-foreground-500 mt-0.5">
            {templates.length} template(s) cadastrado(s) — {templates.filter((t) => t.ativo).length}{' '}
            ativo(s)
          </p>
        </div>
        <button
          onClick={() => {
            setEditTarget(null);
            setShowForm(true);
          }}
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap"
        >
          <i className="ri-add-line"></i>
          Novo Alvará
        </button>
      </div>

      {/* filtros */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1 max-w-sm">
          <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
            <i className="ri-search-line text-foreground-400 text-sm"></i>
          </div>
          <input
            type="text"
            placeholder="Buscar por nome, descrição ou órgão..."
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            className="w-full pl-9 pr-3 py-2.5 rounded-md border border-background-300/60 bg-background-50 text-sm"
          />
        </div>
        <select
          value={filtroCategoria}
          onChange={(e) => setFiltroCategoria(e.target.value)}
          className="px-3 py-2.5 rounded-md border border-background-300/60 bg-background-50 text-sm"
        >
          <option value="">Todas as categorias</option>
          {categorias.map((c) => (
            <option key={c.id} value={c.id}>
              {c.nome}
            </option>
          ))}
        </select>
        <select
          value={filtroEstado}
          onChange={(e) => setFiltroEstado(e.target.value)}
          className="px-3 py-2.5 rounded-md border border-background-300/60 bg-background-50 text-sm"
        >
          <option value="">Todos os estados</option>
          {estados.map((e) => (
            <option key={e.uf} value={e.uf}>
              {e.nome} ({e.uf})
            </option>
          ))}
        </select>
      </div>

      {/* tabela */}
      <div className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-background-200/70 bg-background-100/50">
                <th className="text-left text-xs font-semibold text-foreground-500 px-4 py-3 whitespace-nowrap">
                  Alvará
                </th>
                <th className="text-left text-xs font-semibold text-foreground-500 px-4 py-3 whitespace-nowrap">
                  Categoria
                </th>
                <th className="text-left text-xs font-semibold text-foreground-500 px-4 py-3 whitespace-nowrap hidden md:table-cell">
                  Abrangência
                </th>
                <th className="text-left text-xs font-semibold text-foreground-500 px-4 py-3 whitespace-nowrap hidden lg:table-cell">
                  Estados
                </th>
                <th className="text-left text-xs font-semibold text-foreground-500 px-4 py-3 whitespace-nowrap">
                  Periodicidade
                </th>
                <th className="text-center text-xs font-semibold text-foreground-500 px-4 py-3 whitespace-nowrap">
                  Obrig.
                </th>
                <th className="text-right text-xs font-semibold text-foreground-500 px-4 py-3 whitespace-nowrap">
                  Ações
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-background-200/50">
              {filtrados.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-12 text-center text-sm text-foreground-400">
                    Nenhum template de alvará encontrado.
                  </td>
                </tr>
              ) : (
                filtrados.map((t) => {
                  const nacionaisCount = t.estados.length;
                  return (
                    <tr key={t.id} className="hover:bg-background-100/30 transition-colors">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-lg bg-primary-50 flex items-center justify-center flex-shrink-0">
                            <i className="ri-file-shield-2-line text-primary-600 text-sm"></i>
                          </div>
                          <div className="min-w-0">
                            <p className="text-sm font-semibold text-foreground-800 truncate max-w-[280px]">
                              {t.nome}
                            </p>
                            <p className="text-xs text-foreground-500 truncate">{t.orgaoEmissor}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-medium border ${categoriaBadge(t.categoria)}`}
                        >
                          {t.categoria}
                        </span>
                      </td>
                      <td className="px-4 py-3 hidden md:table-cell">
                        <div className="flex items-center gap-1.5">
                          <span className="text-sm text-foreground-700 capitalize">
                            {t.abrangencia}
                          </span>
                          {!t.todasCidades && t.cidadesEspecificas.length > 0 && (
                            <span className="text-xs text-foreground-500">
                              ({t.cidadesEspecificas.length} cidades)
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 hidden lg:table-cell">
                        <div className="flex flex-wrap gap-1">
                          {t.estados.slice(0, 4).map((uf) => (
                            <span
                              key={uf}
                              className="inline-block px-1.5 py-0.5 rounded text-[10px] font-medium bg-background-100 text-foreground-700 border border-background-200"
                            >
                              {uf}
                            </span>
                          ))}
                          {nacionaisCount > 4 && (
                            <span className="inline-block px-1.5 py-0.5 rounded text-[10px] font-medium text-foreground-500">
                              +{nacionaisCount - 4}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm text-foreground-600 capitalize">
                          {t.periodicidade}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        {t.obrigatorio ? (
                          <div className="w-5 h-5 rounded-full bg-red-50 flex items-center justify-center mx-auto">
                            <i className="ri-alert-fill text-red-600 text-xs"></i>
                          </div>
                        ) : (
                          <span className="text-sm text-foreground-400">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={() => {
                              setEditTarget(t);
                              setShowForm(true);
                            }}
                            className="w-8 h-8 rounded-md flex items-center justify-center text-foreground-500 hover:bg-background-100 hover:text-primary-600 transition-colors"
                            title="Editar"
                          >
                            <i className="ri-pencil-line text-sm"></i>
                          </button>
                          <button
                            onClick={() => setDeleteTarget(t)}
                            className="w-8 h-8 rounded-md flex items-center justify-center text-foreground-500 hover:bg-background-100 hover:text-red-600 transition-colors"
                            title="Excluir"
                          >
                            <i className="ri-delete-bin-line text-sm"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* modal formulário */}
      {showForm && (
        <AlvaraFormModal
          template={editTarget}
          onSave={handleSave}
          onClose={() => {
            setShowForm(false);
            setEditTarget(null);
          }}
          saving={saving}
        />
      )}

      {/* modal exclusão */}
      {deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={() => setDeleteTarget(null)}></div>
          <div className="relative bg-background-50 rounded-lg border border-background-200/70 p-6 w-full max-w-md mx-4 shadow-lg">
            <p className="text-sm font-semibold text-foreground-900 mb-2">Confirmar exclusão</p>
            <p className="text-sm text-foreground-600 mb-6">
              Tem certeza que deseja excluir o template <strong>{deleteTarget.nome}</strong>?<br />
              <span className="text-foreground-500">
                Isso afetará apenas o cadastro de novos condomínios. Dados já existentes não serão
                perdidos.
              </span>
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setDeleteTarget(null)}
                disabled={saving}
                className="px-4 py-2 rounded-md border border-background-300/60 text-sm font-medium text-foreground-700 hover:bg-background-100 transition-colors whitespace-nowrap"
              >
                Cancelar
              </button>
              <button
                onClick={handleDelete}
                disabled={saving}
                className="px-4 py-2 rounded-md bg-red-600 text-white text-sm font-medium hover:bg-red-700 transition-colors whitespace-nowrap disabled:opacity-50"
              >
                {saving ? 'Excluindo...' : 'Excluir'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* toast */}
      {toast && (
        <div className="fixed bottom-6 right-6 z-50">
          <div
            className={`px-4 py-3 rounded-lg text-sm font-medium shadow-lg flex items-center gap-2 ${
              toast.type === 'success'
                ? 'bg-green-50 text-green-800 border border-green-200'
                : 'bg-background-50 text-foreground-800 border border-background-200/70'
            }`}
          >
            <i
              className={`${toast.type === 'success' ? 'ri-checkbox-circle-line text-green-600' : 'ri-information-line text-primary-600'}`}
            ></i>
            {toast.msg}
          </div>
        </div>
      )}
    </div>
  );
}

/* ---------- MODAL DE FORMULÁRIO ---------- */
function AlvaraFormModal({
  template,
  onSave,
  onClose,
  saving,
}: {
  template: AlvaraTemplate | null;
  onSave: (data: AlvaraTemplate) => void;
  onClose: () => void;
  saving: boolean;
}) {
  const { itens: estados } = useCatalogo<Estado>('estados');
  const { itens: categorias } = useCatalogo<CategoriaAlvara>('categorias_alvara');
  const { itens: itensDeInfra } = useCatalogo<ItemDeInfraestrutura>('infraestrutura_itens');
  const janelaDeAlvara = useJanelaDeAlerta('alvara');
  const [form, setForm] = useState({
    nome: template?.nome || '',
    descricao: template?.descricao || '',
    categoria: template?.categoria || 'Bombeiros',
    abrangencia: template?.abrangencia || ('estadual' as AlvaraTemplate['abrangencia']),
    esfera: template?.esfera || ('estadual' as EsferaAlvara),
    estados: template?.estados || ([] as string[]),
    todasCidades: template?.todasCidades ?? true,
    cidadesEspecificas: template?.cidadesEspecificas || ([] as string[]),
    periodicidade: template?.periodicidade || 'anual',
    obrigatorio: template?.obrigatorio ?? true,
    orgaoEmissor: template?.orgaoEmissor || '',
    vigencia: template?.vigencia ?? ({ valor: 1, unidade: 'anos' } as PeriodoParcial),
    prazo: template?.prazo ?? ({ valor: null, unidade: null } as PeriodoParcial),
    requerDocumentoAnexo: template?.requerDocumentoAnexo ?? true,
    diasAntecedenciaAviso: template?.diasAntecedenciaAviso ?? null,
    tempoMedioGeracao:
      template?.tempoMedioGeracao ?? ({ valor: null, unidade: null } as PeriodoParcial),
    aplicaAoCondominioGeral: template?.aplicaAoCondominioGeral ?? true,
    itens: template?.itens || ([] as string[]),
    ativo: template?.ativo ?? true,
  });
  const [erroAlvo, setErroAlvo] = useState<string | null>(null);

  const [novaCidade, setNovaCidade] = useState('');
  const [estadoSearch, setEstadoSearch] = useState('');

  const estadosFiltrados = estados.filter(
    (e) =>
      e.nome.toLowerCase().includes(estadoSearch.toLowerCase()) ||
      e.uf.toLowerCase().includes(estadoSearch.toLowerCase()),
  );

  const toggleEstado = (uf: string) => {
    setForm((prev) => ({
      ...prev,
      estados: prev.estados.includes(uf)
        ? prev.estados.filter((e) => e !== uf)
        : [...prev.estados, uf],
    }));
  };

  const selectAllEstados = () => {
    if (form.estados.length === 27) {
      setForm((prev) => ({ ...prev, estados: [] }));
    } else {
      setForm((prev) => ({ ...prev, estados: estados.map((e) => e.uf) }));
    }
  };

  const addCidade = () => {
    const cidade = novaCidade.trim();
    if (!cidade || form.cidadesEspecificas.includes(cidade)) return;
    setForm((prev) => ({ ...prev, cidadesEspecificas: [...prev.cidadesEspecificas, cidade] }));
    setNovaCidade('');
  };

  const removeCidade = (cidade: string) => {
    setForm((prev) => ({
      ...prev,
      cidadesEspecificas: prev.cidadesEspecificas.filter((c) => c !== cidade),
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.nome.trim() || form.estados.length === 0) return;

    // A mesma regra que a constraint trigger aplica (secao 4.3). Conferir aqui
    // e retorno imediato: sem isso o erro so apareceria no commit, como uma
    // mensagem de banco no meio da tela.
    if (!form.aplicaAoCondominioGeral && form.itens.length === 0) {
      setErroAlvo(
        'Marque "aplica ao condomínio geral" ou vincule ao menos um item de infraestrutura.',
      );
      return;
    }
    setErroAlvo(null);

    const data: AlvaraTemplate = {
      id: template?.id || '',
      nome: form.nome.trim(),
      descricao: form.descricao.trim(),
      categoria: form.categoria,
      abrangencia: form.abrangencia,
      esfera: form.esfera,
      estados: form.estados,
      todasCidades: form.todasCidades,
      cidadesEspecificas: form.todasCidades ? [] : form.cidadesEspecificas,
      periodicidade: form.periodicidade,
      obrigatorio: form.obrigatorio,
      orgaoEmissor: form.orgaoEmissor.trim(),
      vigencia: form.vigencia,
      prazo: form.prazo,
      requerDocumentoAnexo: form.requerDocumentoAnexo,
      diasAntecedenciaAviso: form.diasAntecedenciaAviso,
      tempoMedioGeracao: form.tempoMedioGeracao,
      aplicaAoCondominioGeral: form.aplicaAoCondominioGeral,
      itens: form.itens,
      dataCriacao: template?.dataCriacao || '',
      ativo: form.ativo,
    };
    onSave(data);
  };

  const abrangenciaLabels: Record<string, string> = {
    nacional: 'Nacional — vale para todos os estados',
    estadual: 'Estadual — selecione os estados',
    municipal: 'Municipal — selecione estados e cidades',
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-[5vh] px-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose}></div>
      <div className="relative bg-background-50 rounded-lg border border-background-200/70 w-full max-w-2xl shadow-lg">
        <div className="px-5 py-4 border-b border-background-200/70 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-foreground-900">
            {template ? 'Editar Alvará' : 'Novo Alvará'}
          </h3>
          <button
            onClick={onClose}
            className="w-7 h-7 rounded-md flex items-center justify-center text-foreground-500 hover:bg-background-100"
          >
            <i className="ri-close-line"></i>
          </button>
        </div>

        <form
          onSubmit={handleSubmit}
          className="p-5 flex flex-col gap-4 max-h-[75vh] overflow-y-auto"
        >
          {/* Nome e Categoria */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="sm:col-span-2">
              <label className="block text-sm font-medium text-foreground-700 mb-1.5">
                Nome do Alvará <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={form.nome}
                onChange={(e) => setForm({ ...form, nome: e.target.value })}
                required
                placeholder="Ex: AVCB — Auto de Vistoria do Corpo de Bombeiros"
                className="w-full px-3 py-2.5 rounded-md border border-background-300/60 bg-background-50 text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground-700 mb-1.5">
                Categoria
              </label>
              <select
                value={form.categoria}
                onChange={(e) => setForm({ ...form, categoria: e.target.value })}
                className="w-full px-3 py-2.5 rounded-md border border-background-300/60 bg-background-50 text-sm"
              >
                {categorias.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.nome}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Descrição */}
          <div>
            <label className="block text-sm font-medium text-foreground-700 mb-1.5">
              Descrição
            </label>
            <textarea
              value={form.descricao}
              onChange={(e) => setForm({ ...form, descricao: e.target.value })}
              maxLength={500}
              rows={2}
              placeholder="Descreva o documento, sua finalidade e exigências legais..."
              className="w-full px-3 py-2.5 rounded-md border border-background-300/60 bg-background-50 text-sm resize-y"
            />
            <p className="text-xs text-foreground-400 mt-1">
              {form.descricao.length}/500 caracteres
            </p>
          </div>

          {/* Abrangência */}
          <div>
            <label className="block text-sm font-medium text-foreground-700 mb-1.5">
              Abrangência <span className="text-red-500">*</span>
            </label>
            <div className="flex flex-wrap gap-2">
              {(['nacional', 'estadual', 'municipal'] as const).map((a) => (
                <button
                  key={a}
                  type="button"
                  onClick={() => setForm({ ...form, abrangencia: a })}
                  className={`px-4 py-2 rounded-md text-sm font-medium border transition-colors whitespace-nowrap ${
                    form.abrangencia === a
                      ? 'bg-primary-50 text-primary-700 border-primary-200'
                      : 'bg-background-50 text-foreground-600 border-background-300/60 hover:bg-background-100'
                  }`}
                >
                  {a === 'nacional' ? 'Nacional' : a === 'estadual' ? 'Estadual' : 'Municipal'}
                </button>
              ))}
            </div>
            <p className="text-xs text-foreground-500 mt-1.5">
              {abrangenciaLabels[form.abrangencia]}
            </p>
          </div>

          {/* Seleção de Estados */}
          <div>
            <label className="block text-sm font-medium text-foreground-700 mb-1.5">
              Estados <span className="text-red-500">*</span>
              <span className="text-xs text-foreground-400 font-normal ml-2">
                {form.estados.length} selecionado(s)
              </span>
            </label>
            <div className="mb-2 flex items-center gap-2">
              <div className="relative flex-1">
                <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                  <i className="ri-search-line text-foreground-400 text-sm"></i>
                </div>
                <input
                  type="text"
                  value={estadoSearch}
                  onChange={(e) => setEstadoSearch(e.target.value)}
                  placeholder="Buscar estado..."
                  className="w-full pl-9 pr-3 py-2 rounded-md border border-background-300/60 bg-background-50 text-sm"
                />
              </div>
              <button
                type="button"
                onClick={selectAllEstados}
                className="px-3 py-2 rounded-md border border-background-300/60 text-xs font-medium text-foreground-600 hover:bg-background-100 transition-colors whitespace-nowrap"
              >
                {form.estados.length === 27 ? 'Desmarcar todos' : 'Selecionar todos'}
              </button>
            </div>
            <div className="max-h-36 overflow-y-auto grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-1 p-2 border border-background-200/70 rounded-md bg-background-50">
              {estadosFiltrados.map((e) => {
                const selected = form.estados.includes(e.uf);
                return (
                  <button
                    key={e.uf}
                    type="button"
                    onClick={() => toggleEstado(e.uf)}
                    className={`px-2 py-1.5 rounded text-xs font-medium text-left transition-colors whitespace-nowrap ${
                      selected
                        ? 'bg-primary-500 text-background-50'
                        : 'hover:bg-background-100 text-foreground-600'
                    }`}
                  >
                    {e.uf}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Cidades (se municipal) */}
          <div>
            <label className="flex items-center gap-2 cursor-pointer mb-2">
              <input
                type="checkbox"
                checked={form.todasCidades}
                onChange={(e) => setForm({ ...form, todasCidades: e.target.checked })}
                className="w-4 h-4 rounded border-background-300 text-primary-500 focus:ring-primary-400"
              />
              <span className="text-sm font-medium text-foreground-700">
                Vale para todas as cidades dos estados selecionados
              </span>
            </label>

            {!form.todasCidades && (
              <div className="ml-6">
                <label className="block text-xs font-medium text-foreground-500 mb-1.5">
                  Cidades específicas
                </label>
                <div className="flex items-center gap-2 mb-2">
                  <input
                    type="text"
                    value={novaCidade}
                    onChange={(e) => setNovaCidade(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        addCidade();
                      }
                    }}
                    placeholder="Nome da cidade e pressione Enter"
                    className="flex-1 px-3 py-2 rounded-md border border-background-300/60 bg-background-50 text-sm"
                  />
                  <button
                    type="button"
                    onClick={addCidade}
                    className="px-3 py-2 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
                  >
                    <i className="ri-add-line"></i> Adicionar
                  </button>
                </div>
                {form.cidadesEspecificas.length > 0 && (
                  <div className="flex flex-wrap gap-1.5">
                    {form.cidadesEspecificas.map((cidade) => (
                      <span
                        key={cidade}
                        className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-accent-100 text-accent-900 border border-accent-300"
                      >
                        {cidade}
                        <button
                          type="button"
                          onClick={() => removeCidade(cidade)}
                          className="w-4 h-4 flex items-center justify-center text-accent-600 hover:text-accent-800"
                        >
                          <i className="ri-close-line text-xs"></i>
                        </button>
                      </span>
                    ))}
                  </div>
                )}
                {form.abrangencia !== 'municipal' && (
                  <p className="text-xs text-amber-600 mt-1 flex items-center gap-1">
                    <i className="ri-information-line"></i>A abrangência selecionada é "
                    {form.abrangencia}". Cidades específicas só fazem sentido com abrangência
                    "municipal".
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Esfera e órgão emissor */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground-700 mb-1.5">
                Esfera — quem emite
              </label>
              <select
                value={form.esfera}
                onChange={(e) => setForm({ ...form, esfera: e.target.value as EsferaAlvara })}
                className="w-full px-3 py-2.5 rounded-md border border-background-300/60 bg-background-50 text-sm"
              >
                {ESFERAS.map((op) => (
                  <option key={op.valor} value={op.valor}>
                    {op.rotulo}
                  </option>
                ))}
              </select>
              <p className="text-xs text-foreground-500 mt-1">
                Diferente da abrangência acima, que diz onde o alvará é exigido.
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground-700 mb-1.5">
                Órgão Emissor
              </label>
              <input
                type="text"
                value={form.orgaoEmissor}
                onChange={(e) => setForm({ ...form, orgaoEmissor: e.target.value })}
                placeholder="Ex: Corpo de Bombeiros, Prefeitura..."
                className="w-full px-3 py-2.5 rounded-md border border-background-300/60 bg-background-50 text-sm"
              />
            </div>
          </div>

          {/* Vigência e prazo como Período (componente 3.1) */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <CampoPeriodo
              rotulo="Vigência do documento"
              valor={form.vigencia}
              onChange={(vigencia) => setForm({ ...form, vigencia })}
              ajuda="Validade depois de emitido."
              textoVazio="documento que não vence (Habite-se, Alvará de Construção)."
            />
            <CampoPeriodo
              rotulo="Prazo para obter"
              valor={form.prazo}
              onChange={(prazo) => setForm({ ...form, prazo })}
              ajuda="Janela entre a exigência nascer e o documento ter de estar na mão."
              textoVazio="sem janela definida."
            />
          </div>

          <CampoAvisoVencimento
            diasAntecedencia={form.diasAntecedenciaAviso}
            onChangeDias={(diasAntecedenciaAviso) => setForm({ ...form, diasAntecedenciaAviso })}
            tempoMedioGeracao={form.tempoMedioGeracao}
            onChangeTempoMedio={(tempoMedioGeracao) => setForm({ ...form, tempoMedioGeracao })}
            diasPadraoGlobal={janelaDeAlvara}
          />

          {/* A que o alvará se aplica (secao 4.3) */}
          <fieldset className="border border-background-300/60 rounded-md p-4">
            <legend className="px-1.5 text-sm font-medium text-foreground-700">
              A que este alvará se aplica
            </legend>

            <label className="flex items-start gap-2 cursor-pointer select-none mb-3">
              <input
                type="checkbox"
                checked={form.aplicaAoCondominioGeral}
                onChange={(e) => setForm({ ...form, aplicaAoCondominioGeral: e.target.checked })}
                className="w-4 h-4 mt-0.5 rounded border-background-300 text-primary-500 focus:ring-primary-400"
              />
              <span className="text-sm text-foreground-700">
                Cobre o condomínio inteiro
                <span className="block text-xs text-foreground-500">
                  Pode valer junto com itens específicos: a Licença da Vigilância Sanitária cobre o
                  prédio e também é exigida por causa da piscina.
                </span>
              </span>
            </label>

            <p className="text-xs text-foreground-500 mb-2">
              Itens de infraestrutura que exigem este alvará. Marcar aqui é o que faz a aba de
              alvarás deixar de cobrar licença de piscina de quem não tem piscina.
            </p>
            <div className="flex flex-wrap gap-2">
              {itensDeInfra.length === 0 ? (
                <p className="text-xs text-foreground-500">
                  Nenhum item no catálogo de infraestrutura.
                </p>
              ) : (
                itensDeInfra.map((item) => {
                  const marcado = form.itens.includes(item.id);
                  return (
                    <button
                      key={item.id}
                      type="button"
                      onClick={() =>
                        setForm({
                          ...form,
                          itens: marcado
                            ? form.itens.filter((id) => id !== item.id)
                            : [...form.itens, item.id],
                        })
                      }
                      aria-pressed={marcado}
                      className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${
                        marcado
                          ? 'bg-primary-500 text-background-50 border-primary-500'
                          : 'bg-background-50 text-foreground-700 border-background-300'
                      }`}
                    >
                      {item.nome}
                    </button>
                  );
                })
              )}
            </div>

            {erroAlvo && (
              <p role="alert" className="text-xs text-red-600 mt-2">
                {erroAlvo}
              </p>
            )}
          </fieldset>

          {/* Checkboxes */}
          <div className="flex flex-wrap items-center gap-6 pt-2">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={form.obrigatorio}
                onChange={(e) => setForm({ ...form, obrigatorio: e.target.checked })}
                className="w-4 h-4 rounded border-background-300 text-primary-500 focus:ring-primary-400"
              />
              <span className="text-sm font-medium text-foreground-700">
                <i className="ri-alert-fill text-red-600 mr-1"></i>
                Obrigatório
              </span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={form.requerDocumentoAnexo}
                onChange={(e) => setForm({ ...form, requerDocumentoAnexo: e.target.checked })}
                className="w-4 h-4 rounded border-background-300 text-primary-500 focus:ring-primary-400"
              />
              <span className="text-sm font-medium text-foreground-700">
                <i className="ri-attachment-2 text-foreground-500 mr-1"></i>
                Exige documento anexado
              </span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={form.ativo}
                onChange={(e) => setForm({ ...form, ativo: e.target.checked })}
                className="w-4 h-4 rounded border-background-300 text-primary-500 focus:ring-primary-400"
              />
              <span className="text-sm font-medium text-foreground-700">Ativo</span>
            </label>
          </div>

          {/* Botões */}
          <div className="flex justify-end gap-3 pt-2 border-t border-background-200/70">
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="px-4 py-2.5 rounded-md border border-background-300/60 text-sm font-medium text-foreground-700 hover:bg-background-100 transition-colors whitespace-nowrap"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap disabled:opacity-50"
            >
              {saving ? 'Salvando...' : template ? 'Salvar Alterações' : 'Cadastrar Alvará'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ---------- MAIN PAGE ---------- */
export default function AdminAlvarasPage() {
  const { isSuperuser } = useAuth();

  if (!isSuperuser) {
    return (
      <DashboardLayout>
        <div className="max-w-6xl flex flex-col items-center justify-center py-20">
          <div className="w-16 h-16 rounded-full bg-red-50 flex items-center justify-center mb-4">
            <i className="ri-lock-line text-red-600 text-2xl"></i>
          </div>
          <h2 className="text-lg font-bold text-foreground-900 mb-2">Acesso Restrito</h2>
          <p className="text-sm text-foreground-500 text-center max-w-md">
            Esta área é reservada para o superusuário administrador da plataforma.
          </p>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-6xl">
        <div className="mb-6">
          <h1 className="text-xl md:text-2xl font-bold text-foreground-900">
            Alvarás — Configuração
          </h1>
          <p className="text-sm text-foreground-500 mt-0.5">
            Cadastre e gerencie os templates de alvarás e licenças por estado e cidade. Esses
            templates serão refletidos automaticamente em cada condomínio conforme seu CEP.
          </p>
        </div>
        <AlvarasConfigTab />
      </div>
    </DashboardLayout>
  );
}
